import { GroupItem } from "common/types/graphql"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { Check, Loader2 } from "lucide-react"
import clsx from "classnames"
import { parseFrequency } from "../../utils/parseFrequency"
import { Thumbnail } from "../Thumbnail"

export interface NewsletterCardProps {
  newsletter: GroupItem
  selected: boolean
  loading: boolean
  isSubscribed: boolean
  onToggle: () => void
  regionCode: string
}

/**
 * Presentational newsletter card component for v2 design.
 * Displays newsletter information and handles user interactions via callbacks.
 */
export const NewsletterCard = ({
  newsletter,
  selected,
  loading,
  isSubscribed,
  onToggle,
  regionCode,
}: NewsletterCardProps) => {
  const t = useTranslations("newsletters")
  const parsedFrequency = parseFrequency(newsletter.frequency)

  return (
    <div className="group relative">
      <div
        className={clsx(
          "relative flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-neutral-50 text-left transition-colors md:flex-row",
          "hover:border-gray-400 hover:bg-white",
          "focus-within:ring-2 focus-within:ring-black focus-within:ring-offset-2",
          {
            "bg-white": selected && !loading,
            "!border-black ring-1 ring-black": selected && !isSubscribed,
          },
        )}
        data-testid="newsletter-card"
      >
        <button
          onClick={onToggle}
          disabled={loading}
          aria-busy={loading}
          aria-pressed={selected}
          aria-label={t(selected ? "button.unsubscribe_from" : "button.subscribe_to", {
            name: newsletter.name,
          })}
          className="absolute inset-0 z-0 h-full w-full cursor-pointer outline-none"
        />

        {/* Selection Indicator */}
        <div className="pointer-events-none relative ps-6 pe-0 pt-6">
          <div
            className={clsx(
              "relative flex h-10 w-10 items-center justify-center rounded-full border border-gray-200",
              {
                "!border-black bg-black text-white": selected && !isSubscribed,
                "group-hover:border-gray-400": !selected,
                "border-none bg-green-300 text-black": selected && isSubscribed && !loading,
              },
            )}
            data-testid="newsletter-card-selection"
          >
            {loading ? (
              <Loader2 className="size-5 motion-safe:animate-spin" />
            ) : selected ? (
              <Check className="size-6" strokeWidth={1.5} />
            ) : (
              <Check
                className="hidden size-6 text-gray-600 group-focus-within:block group-hover:block"
                strokeWidth={1.5}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="pointer-events-none relative min-w-0 flex-1 space-y-1 p-6 pt-4 sm:pt-6">
          <div className="relative inline-flex items-center gap-2">
            <h3 className="text-xl font-medium">{newsletter.name}</h3>
          </div>
          <p className="text-md leading-6">{newsletter.pitch}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-sm">
            <span className="text-sm text-red-400">{parsedFrequency.type}</span>
            {parsedFrequency.details && (
              <>
                <span className="text-gray-600">•</span>
                <span className="text-gray-600">{parsedFrequency.details}</span>
              </>
            )}
            {newsletter.previewUrl && (
              <>
                <span
                  aria-hidden="true"
                  className="text-gray-600 opacity-100 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
                >
                  •
                </span>
                <a
                  aria-label={`${t("button.preview")} ${newsletter.name}`}
                  href={newsletter.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pointer-events-auto relative text-left opacity-100 md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100"
                >
                  <span className="text-indigo-500 hover:underline">{t("button.preview")}</span>
                  <span className="sr-only">{t("button.opens_in_new_tab")}</span>
                </a>
              </>
            )}
          </div>
        </div>

        {/* Newsletter thumbnail mapped from static assets */}
        <Thumbnail
          regionCode={regionCode}
          newsletterName={newsletter.name}
          className="pointer-events-none absolute top-0 right-0 h-20 w-20 flex-shrink-0 overflow-hidden rounded-bl-2xl bg-cover bg-center bg-no-repeat md:static md:h-auto md:w-36 md:rounded-none"
        />
      </div>
    </div>
  )
}
